import classNames from "classnames";
import split from "lodash/split";
import React, { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { fontTagToCssClass } from "../../../../agreed/fonts";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { findFontDefinition } from "../../../../utils/customization/finders";
import { constrainText } from "../../../../utils/customization/text";
import "./style.scss";

interface Props {
  undoKey?: string;
}

const MAX_CHARACTERS = 25;

const EditableBookTitle: FunctionComponent<Props> = ({ undoKey }) => {
  const { configuration, content, dispatch, template } = useCustomizationContext();
  const intl = useIntl();

  const [charsCount, setCharsCount] = useState<number>(0);
  const [charsCountVisibility, setCharsCountVisibility] = useState<boolean>(false);

  const inputTitle = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (content.templateType !== "book") {
      return;
    }
    const initCharsCount = content.bookTitle ? split(content.bookTitle, "").length : 0;
    setCharsCount(initCharsCount);
  }, [content]);

  useEffect(() => {
    if (inputTitle && inputTitle.current) {
      inputTitle.current.focus();
    }
  }, [inputTitle]);

  if (content.templateType !== "book") {
    return null;
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const bookTitle = constrainText(event.target.value, 1, MAX_CHARACTERS, content.bookTitle || "");
    dispatch({
      bookTitle,
      options: configuration?.options,
      type: "changeBookTitle",
      undoKey: undoKey || "changeBookTitle",
    });
  };

  const onFocus = (focus: boolean) => {
    setCharsCountVisibility(focus);
  };

  const fontDefinition =
    content.bookTitleFontKey && findFontDefinition(content.bookTitleFontKey, template);
  const fontClass = fontDefinition ? fontTagToCssClass(fontDefinition.fontTag) : undefined;

  return (
    <div className="EditableBookTitle">
      <input
        ref={inputTitle}
        type="text"
        className={classNames("EditableBookTitle-content", fontClass)}
        onBlur={() => {
          onFocus(false);
        }}
        onChange={onChange}
        onFocus={() => {
          onFocus(true);
        }}
        placeholder={intl.formatMessage({
          id: "kustom_EditableBookTitle_placeholder",
        })}
        title={intl.formatMessage({
          id: "kustom_EditableBookTitle_description",
        })}
        value={content.bookTitle ? content.bookTitle : ""}
      />
      <span
        className={classNames("EditableBookTitle-countChars", {
          "EditableBookTitle-countChars--validation": charsCount <= MAX_CHARACTERS,
          "EditableBookTitle-countChars--hidden": charsCountVisibility === false,
        })}
      >
        {charsCount}/{MAX_CHARACTERS}
      </span>
    </div>
  );
};

export default EditableBookTitle;
